import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Button,
  Switch,
  Badge
} from "./UI";
import { Shield, Smartphone, Monitor, AlertTriangle, Check, Eye, EyeOff, Lock, Loader2 } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";

const sessions = [
  { device: "Chrome on Windows", location: "Bangalore, India", current: true, lastActive: "Now" },
  { device: "Safari on iPhone", location: "Bangalore, India", current: false, lastActive: "2 hours ago" },
  { device: "Chrome on MacOS", location: "Mumbai, India", current: false, lastActive: "3 days ago" },
];

export function SecuritySection() {
  const { addAlert } = useAlert();
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);
  const [securityAlerts, setSecurityAlerts] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [id]: value }));
  };

  const handlePasswordChange = async () => {
    try {
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        addAlert("Please fill in all password fields.", "error");
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        addAlert("New passwords don't match.", "error");
        return;
      }

      if (passwordData.newPassword.length < 6) {
        addAlert("Password must be at least 6 characters long.", "error");
        return;
      }

      setIsLoading(true);
      const access = localStorage.getItem("access");

      const response = await fetch(`${BASE_URL}/auth/change-password/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${access}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        addAlert("Password changed successfully. Please login again.", "success");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        // Handle logout after success
        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/login";
        }, 2000);
      } else {
        addAlert(result.message || "Failed to change password.", "error");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      addAlert("An error occurred while changing your password.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-primary-text">Security</h2>
        <p className="text-text-gray mt-1">Protect your account with these security settings.</p>
      </div>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="h-5 w-5 text-sm-primary" />
            Change Password
          </CardTitle>
          <CardDescription>Update your password regularly to keep your account secure.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Current Password</Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Enter current password"
                value={passwordData.currentPassword}
                onChange={handleInputChange}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-gray hover:text-primary-text"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={passwordData.newPassword}
                onChange={handleInputChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={passwordData.confirmPassword}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <Button onClick={handlePasswordChange} disabled={isLoading} className="gap-2">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Update Password
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-sm-primary" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>Add an extra layer of security to your account.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-bg/30">
            <div className="flex items-center gap-4">
              <div className={`p-2 rounded-lg ${twoFactor ? 'bg-light-green' : 'bg-gray-bg'}`}>
                <Shield className={`h-5 w-5 ${twoFactor ? 'text-dark-green' : 'text-text-gray'}`} />
              </div>
              <div>
                <p className="font-medium text-primary-text">Enable 2FA</p>
                <p className="text-sm text-text-gray">Use an authenticator app for extra security.</p>
              </div>
            </div>
            <Switch checked={twoFactor} onCheckedChange={setTwoFactor} />
          </div>
          {!twoFactor && (
            <p className="text-sm text-sm-accent flex items-center gap-2 mt-4">
              <AlertTriangle className="h-4 w-4" />
              We recommend enabling 2FA for better account protection.
            </p>
          )}
        </CardContent>
      </Card> */}

      {/* <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Monitor className="h-5 w-5 text-sm-primary" />
            Active Sessions
          </CardTitle>
          <CardDescription>Manage devices where you're logged in.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {sessions.map((session, index) => (
            <div key={index} className="flex items-center justify-between p-4 rounded-lg border border-sm-border">
              <div className="flex items-center gap-4">
                <div className="p-2 rounded-lg bg-gray-bg">
                  <Monitor className="h-5 w-5 text-text-gray" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-primary-text">{session.device}</p>
                    {session.current && (
                      <Badge variant="success" className="border-0 text-xs">
                        This device
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-text-gray">{session.location} • {session.lastActive}</p>
                </div>
              </div>
              {!session.current && (
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10">
                  Revoke
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card> */}

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Security Alerts</CardTitle>
          <CardDescription>Get notified about important security events.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 rounded-lg bg-gray-bg/30">
            <div>
              <p className="font-medium text-primary-text">Email alerts for suspicious activity</p>
              <p className="text-sm text-text-gray">We'll notify you of unusual login attempts.</p>
            </div>
            <Switch checked={securityAlerts} onCheckedChange={setSecurityAlerts} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
